import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import Stars from "@/components/Stars";
import ReviewReply from "@/components/ReviewReply";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";

const toSlides = (d) => [
  ...d.site.map((r) => ({ key: r.id, source: "Avis client vérifié", author: r.author, rating: r.rating, text: r.comment, reply: r.reply, sub: r.type === "product" ? r.product_name : "Sur le site" })),
  ...(d.google?.reviews || []).map((r, i) => ({ key: `g${i}`, source: "Google", author: r.author, photo: r.photo, rating: r.rating, text: r.text, sub: r.when, uri: r.uri })),
  ...(d.facebook?.reviews || []).map((r, i) => ({ key: `f${i}`, source: "Facebook", author: r.author, text: r.text, sub: "Recommande Pétales de Miel" })),
];

function Slide({ s }) {
  return (
    <div data-testid={`review-slide-${s.key}`} className="flex h-full flex-col rounded-2xl border border-cream/10 bg-cream/5 p-5">
      <div className="flex items-center gap-3">
        {s.photo ? <img src={s.photo} alt="" referrerPolicy="no-referrer" className="h-9 w-9 rounded-full" /> : <span className="flex h-9 w-9 items-center justify-center rounded-full bg-honey/20 font-serif text-honey">{s.author[0]}</span>}
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-cream">{s.author}</p>
          {s.rating ? <Stars value={s.rating} size={12} emptyClass="text-cream/25" /> : <p className="text-xs text-cream/50">{s.sub}</p>}
        </div>
      </div>
      <div className="flex-1">
        <p className="mt-3 line-clamp-5 text-sm leading-relaxed text-cream/75">« {s.text} »</p>
        <ReviewReply text={s.reply} dark testid={`review-slide-reply-${s.key}`} />
      </div>
      <p className="mt-3 flex items-center justify-between gap-2 text-[11px] uppercase tracking-wider text-cream/40">
        <span className="truncate">{s.source}{s.rating ? ` · ${s.sub}` : ""}</span>
        {s.uri && <a href={s.uri} target="_blank" rel="noopener noreferrer" className="shrink-0 normal-case tracking-normal text-honey/80 hover:text-honey">Voir sur Google Maps</a>}
      </p>
    </div>
  );
}

export default function ReviewsCarousel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reviews/carousel").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return null;
  const slides = toSlides(data);
  if (!slides.length) return null;
  const g = data.google;

  return (
    <section data-testid="reviews-carousel" className="mx-auto max-w-7xl border-b border-cream/10 px-4 pb-12 pt-14 sm:px-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h4 className="eyebrow mb-2 !text-amber-glow">Ils nous font confiance</h4>
          <p className="font-serif text-2xl text-cream">Ce que disent nos clients</p>
          {g?.rating && (
            <p data-testid="reviews-google-rating" className="mt-1 flex items-center gap-2 text-sm text-cream/70">
              <Stars value={g.rating} size={14} emptyClass="text-cream/25" /> {g.rating.toLocaleString("fr-FR")} / 5 sur Google · {g.count} avis
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {data.google_url && <a data-testid="reviews-google-link" href={data.google_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-cream/20 px-4 py-2 text-xs font-medium text-cream transition-colors hover:border-honey hover:text-honey">Laisser un avis Google <ExternalLink size={12} /></a>}
          {data.facebook_url && <a data-testid="reviews-facebook-link" href={data.facebook_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-cream/20 px-4 py-2 text-xs font-medium text-cream transition-colors hover:border-honey hover:text-honey">Avis Facebook <ExternalLink size={12} /></a>}
        </div>
      </div>
      <Carousel opts={{ align: "start", loop: slides.length > 3 }} className="px-1">
        <CarouselContent>
          {slides.map((s) => <CarouselItem key={s.key} className="basis-[85%] sm:basis-1/2 lg:basis-1/3"><Slide s={s} /></CarouselItem>)}
        </CarouselContent>
        {slides.length > 1 && (
          <div className="mt-5 flex justify-end gap-2">
            <CarouselPrevious data-testid="reviews-prev" className="static translate-y-0 border-cream/20 bg-transparent text-cream hover:bg-cream/10 hover:text-cream" />
            <CarouselNext data-testid="reviews-next" className="static translate-y-0 border-cream/20 bg-transparent text-cream hover:bg-cream/10 hover:text-cream" />
          </div>
        )}
      </Carousel>
      {g && <p className="mt-3 text-[11px] text-cream/40">Avis Google fournis par Google Maps.</p>}
    </section>
  );
}
